import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { marker } from '@common/translate-marker';
import { HoldDetailsModalComponent } from '@common/modals/hold-details-modal/hold-details-modal.component';
import { CodelistDisplayModel } from '@common/models/common/CodelistDisplay.model';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { ServiceRequestItemsHoldModalComponent } from 'app/tos-operations/service-request/modals/service-request-items-hold-modal/service-request-items-hold-modal.component';
import { ToastService } from '@services/toast.service';
import { filter, Observable } from 'rxjs';
import { ModalService } from './modal.service';

interface HoldModalOptions {
    entityType: string;
    serviceRequestId?: number;
    containerVisitId?: number;
    serviceRequestVersionShipmentId?: number;
    serviceRequestVersionContainerId?: number;
    serviceRequestVersionShipmentDetailId?: number;
    shipmentPoolId?: number;
    shipmentPoolItemId?: number;
}
export interface HoldModalData {
    serviceRequestVersionId?: number;
    serviceRequestIds?: any[];
    serviceRequestItemIds?: any[];
    shipmentItemIds?: any[];
    containerItemIds?: any[];
    containerVisitIds?: any[];
    shipmentPoolIds?: any[];
    shipmentPoolItemIds?: any[];
    shipmentType?: string;
}

@Injectable({
    providedIn: 'root'
})
export class CustomsService {
    constructor(
        private http: HttpClient,
        private dialogService: ModalService,
        private translateService: TranslateService,
        private toastyService: ToastService
    ) {}

    getDeclarationsList(filter: any): Observable<any> {
        return this.http.post('api:///Customs/GetDeclarationsList', filter);
    }

    getDeclaration(id: number): Observable<any> {
        return this.http.get<any>(`api:///Customs/GetDeclaration/${id}`);
    }

    getDeclarationByMrn(mrn: string): Observable<any> {
        return this.http.get<any>(`api:///Customs/GetDeclarationByMrn/${mrn}`);
    }

    getCargos(filter: any): Observable<any> {
        return this.http.post<any>(`api:///Customs/GetCargos`, filter);
    }

    getTaricHierarchy(taricId: string): Observable<CodelistDisplayModel[]> {
        return this.http.post<CodelistDisplayModel[]>(`api:///Customs/GetTaricHierarchy`, { taricId });
    }

    getRealizedQuantities(mrn: string): Observable<any> {
        return this.http.get<any>(`api:///Customs/GetRealizedQuantities/${mrn}`);
    }

    getRealizedQuantitiesOnCargoTab(mrnId: number): Observable<any> {
        return this.http.get<any>(`api:///Customs/GetRealizedQuantitiesOnCargoTab/${mrnId}`);
    }

    getServiceRequestsForDeclaration(mrnId: number): Observable<any> {
        return this.http.get<any>(`api:///Customs/GetServiceRequestsForDeclaration/${mrnId}`);
    }

    manualGetMessage(model: any): Observable<any> {
        return this.http.post('api:///Customs/ManualGetMessage', model);
    }

    getCustomsHoldDetails(filter: any): Observable<any> {
        return this.http.post<any>(`api:///Customs/GetCustomsHoldDetails`, filter);
    }

    saveCustomsHoldDetails(model: any): Observable<any> {
        return this.http.post('api:///Customs/SaveCustomsHoldDetails', model);
    }

    unholdSelected(filteredData: any[], userRoles: any[], options: HoldModalData) {
        // couldn't come with a better name. This is to check if there is unholdRoleId for the selected customs hold type
        const isUnholdRoleIdAvailable = filteredData?.some(
            (x) => x?.unholdRoles?.some((y) => userRoles?.some((z) => z.roleId === y?.unholdRoleId))
        );

        const activeHoldTypeIds = isUnholdRoleIdAvailable
            ? filteredData.map(
                  (item) =>
                      item?.unholdRoles
                          ?.filter((y) => userRoles?.some((z) => z.roleId === y?.unholdRoleId))
                          .map((filteredRole) => filteredRole.cargoHoldTypeId)
              )
            : filteredData.map((item) => item?.activeHoldTypeIds);

        return this.dialogService
            .holdServiceRequestItems(
                ServiceRequestItemsHoldModalComponent,
                this.translateService.instant(marker('Unhold Selected Items?')),
                null,
                this.translateService.instant(marker('Unhold')),
                null,
                this.translateService.instant(marker('Cancel')),
                options.serviceRequestVersionId,
                options.serviceRequestItemIds,
                options.shipmentItemIds,
                options.containerItemIds,
                options.containerVisitIds,
                options.shipmentPoolIds,
                options.shipmentPoolItemIds,
                true,
                options.shipmentType,
                activeHoldTypeIds,
                isUnholdRoleIdAvailable,
                options.serviceRequestIds
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)));
    }

    holdSelected(options: HoldModalData) {
        return this.dialogService
            .holdServiceRequestItems(
                ServiceRequestItemsHoldModalComponent,
                this.translateService.instant(marker('Hold Selected Items?')),
                null,
                this.translateService.instant(marker('Hold')),
                null,
                this.translateService.instant(marker('Cancel')),
                options.serviceRequestVersionId,
                options.serviceRequestItemIds,
                options.shipmentItemIds,
                options.containerItemIds,
                options.containerVisitIds,
                options.shipmentPoolIds,
                options.shipmentPoolItemIds,
                false,
                options.shipmentType,
                undefined,
                undefined,
                options.serviceRequestIds
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)));
    }

    isUnholdButtonDisabled(
        data: any[],
        selected: any[],
        hasHoldPermission: boolean,
        hasUnholdPerRolePermission: boolean,
        userRoles: any[]
    ): boolean {
        if (!data) return false;

        const haveCustomsHold =
            hasHoldPermission && data.filter((x) => selected?.includes(x.id)).every((x) => x.customsHold);
        const hasUnholdPermission =
            hasUnholdPerRolePermission &&
            data.some(
                (x) =>
                    selected?.includes(x.id) &&
                    x?.unholdRoles?.some((y) => userRoles?.some((z) => z.roleId === y.unholdRoleId))
            );

        return (haveCustomsHold || hasUnholdPermission) && selected?.length > 0;
    }

    openHoldModal(dataItem: any, hasPermission: boolean, options: HoldModalOptions) {
        if (!hasPermission) return;

        if (!dataItem.customsHold && dataItem.activeHold !== false) return;

        // gray icon should be displayed even if some of their items have been held, but the model shouldn't be opened
        if (
            ['Service Request', 'Shipment Pool'].includes(options.entityType) &&
            !dataItem?.showInactiveHolds &&
            !dataItem.customsHold
        )
            return;

        const title = this.translateService.instant(marker('Customs Hold'));
        const saveButton = this.translateService.instant(marker('Save'));
        const cancelButton = this.translateService.instant(marker('Cancel'));

        this.dialogService
            .openHoldDetailsModal(
                HoldDetailsModalComponent,
                title,
                null,
                saveButton,
                null,
                cancelButton,
                dataItem.id,
                options.entityType,
                options.serviceRequestId,
                options.containerVisitId,
                options.serviceRequestVersionShipmentId,
                options.serviceRequestVersionContainerId,
                options.serviceRequestVersionShipmentDetailId,
                options.shipmentPoolId,
                options.shipmentPoolItemId
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)))
            .subscribe(() => {
                this.toastyService.success(this.translateService.instant(marker('Hold successfully updated.')));
            });
    }
}
